import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, Renderer2, RendererStyleFlags2, ViewChild } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { ActivatedRoute } from '@angular/router';
import { fromUserActions, UserState } from '@features/account/+state';
import { Store } from '@ngrx/store';
import { NotificationsDialogComponent } from '@shared/components/dialogs/notifications-dialog/notifications-dialog.component';
import { FooterState } from '@shared/enums/footer-state.enum';
import { VisibilityState } from '@shared/enums/visibility-state.enum';
import { AuthUser } from '@shared/models/auth-user.model';
import { BrandPackService } from '@shared/services/brand-pack/brand-pack.service';
import { IUserNotification } from '@shared/models/misc/misc';
import { DatalayerService } from '@shared/services';
import { AteModalService } from '@shared/services/ate-modal/ate-modal.service';
import { FcmNotificationsService } from '@shared/services/fcm-notifications/fcm-notifications.service';
import { FilterService } from '@shared/services/filters/filter.service';
import { FooterService } from '@shared/services/footer/footer.service';
import { FullPageViewService } from '@shared/services/full-page-view/full-page-view.service';
import { NotificationsService } from '@shared/services/notifications/notifications.service';
import { WindowService } from '@shared/services/window/window.service';
import { appQuery, fromAppActions } from '@shared/state';
import { fromApplicationActions } from '@shared/state/actions/application/application.actions';
import { ApplicationState } from '@shared/state/reducers/application/application.reducer';
import { applicationQuery } from '@shared/state/selectors/application/application.selectors';
import { authenticationQuery } from '@shared/state/selectors/authentication/authentication.selectors';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, first, map, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'marketplace-supplier',
  templateUrl: './supplier.component.html',
  styleUrls: ['./supplier.component.scss'],
  standalone: false
})
export class SupplierComponent implements OnInit, AfterViewInit, OnDestroy {
  isFullPageView$: Observable<boolean>;
  footerState$: Observable<string>;
  headerState$: Observable<boolean>;
  overflowState$: Observable<boolean>;
  notificationsMenuOpen$: Observable<boolean>;
  notifications$: Observable<IUserNotification[]>;
  footerState = FooterState;
  currentDate: Date = new Date();
  isMobile = false;
  sidebarVisible;
  filtersActive: boolean;
  endDrawerOpen = false;
  showNotificationsDrawer = false;
  innerHeight: number;
  innerWidth: number;
  subfiltersStatus$: Subscription;
  subscriptionList = new Subscription();
  showHeader = true;
  hideOverflow = false;

  @ViewChild('header', { static: false }) header: ElementRef;
  @ViewChild('container', { static: false }) container: ElementRef;
  @ViewChild('drawer', { static: false }) drawer: MatDrawer;
  @ViewChild('drawerFilters', { static: false }) drawerFilters: MatDrawer;
  private _destroy$ = new Subject();

  constructor(
    private _windowService: WindowService,
    private _footerService: FooterService,
    private _fullPageViewService: FullPageViewService,
    private _store: Store,
    private _applicationStore: Store<ApplicationState>,
    private _dataLayer: DatalayerService,
    private _filterService: FilterService,
    private _renderer: Renderer2,
    private _notificationsService: NotificationsService,
    private _fcmNotificationsService: FcmNotificationsService,
    private _userStore: Store<UserState>,
    private _ateModalService: AteModalService,
    private _route: ActivatedRoute,
    private _brandPackService: BrandPackService
  ) {
    this.onResize();
  }

  @HostListener('window:resize')
  onResize() {
    this.innerWidth = window.innerWidth;
    this.innerHeight = window.innerHeight;
    this.isMobile = this._windowService.isMobile();
    this.setInnerHeight();

    if (this.isMobile) {
      this.closeSidebar();
    } else {
      this._applicationStore.dispatch(new fromApplicationActions.SetSidebar());
    }
  }

  ngOnInit() {
    this.setInnerHeight();
    window.visualViewport?.addEventListener('resize', this._onVisualViewportResize);
    this.ensureListingLoaded();
    this.applyBrandPack();
    this.getNotifications();
    this.getNotificationsMenuState();
    this.isFullPageView$ = this._fullPageViewService.getState$();
    this.footerState$ = this._footerService.getState$();
    this.headerState$ = this._store.select(applicationQuery.getShowHeader);
    this.overflowState$ = this._store.select(applicationQuery.getPageOverflow);
    this.listenToSidebarVisibility();
    this.initialiseSidebarVisibility();
    this.getFiltersStatus();

    if (this.isMobile) {
      this.hideHeaderOnScroll();
    }

    // @todo: Revisit the notification logic with respect to the new CRM, as users will
    // expect to see relevant notifications if they click yes.
    // switch (window.Notification?.permission) {
    //   case 'default':
    //     this.openFCMNotificationsDialog();
    //     break;
    //   case 'granted':
    //     this.updateToken();
    //     break;
    // }
  }

  ngAfterViewInit() {
    this._dataLayer.sendEvent('pageInitialised');

    this.notifications$ = this._store.select(applicationQuery.getUserNotifications);
    this.notifications$.pipe(first()).subscribe(n => console.log(n));
  }

  /**
   * If touch events are fired, we hide the header when scrolling down,
   * then show it again on scroll up.
   */
  hideHeaderOnScroll() {
    let lastTouchY = 0; // Store the last touch Y position
    let isHeaderVisible; // Track the current header visibility state
    const deadZone = 10; // The area each way that is ignored to avoid jittering.

    document.addEventListener(
      'touchstart',
      event => {
        if (!this.isMobile) {
          return;
        }
        if (event.touches[0]) {
          lastTouchY = event.touches[0].pageY;
        }
      },
      { passive: true }
    );

    document.addEventListener(
      'touchmove',
      event => {
        if (!this.isMobile || !event.touches || !event.touches.length) {
          return;
        }

        if (document.body.classList.contains('ate-modal-open')) {
          return;
        }

        const currentTouchY = event.touches[0].pageY;

        // Ignore touches in the deadzone.
        if (Math.abs(currentTouchY - lastTouchY) <= deadZone) {
          return;
        }

        const isScrollingUp = currentTouchY > lastTouchY;

        if (isScrollingUp !== isHeaderVisible) {
          this._applicationStore.dispatch(new fromApplicationActions.SetShowHeaderState(isScrollingUp));
          isHeaderVisible = isScrollingUp;
        }
        lastTouchY = currentTouchY;
      },
      { passive: true }
    );
  }

  getFiltersStatus() {
    this.subfiltersStatus$ = this._filterService.filtersActive$.subscribe({
      next: response => {
        this.filtersActive = response;
        if (!this.showNotificationsDrawer) {
          this.endDrawerOpen = response;
        }
        this.subscriptionList.add(this.subfiltersStatus$);
      }
    });
  }

  /**
   * Returns an observable for the notifications menu open state.
   */
  getNotificationsMenuState() {
    this.notificationsMenuOpen$ = this._store.select(applicationQuery.getNotificationsMenuOpen);
    this.subscriptionList.add(
      this.notificationsMenuOpen$.pipe(takeUntil(this._destroy$)).subscribe(isOpen => {
        if (this.isMobile) {
          this.showNotificationsDrawer = isOpen;
          if (isOpen) this.endDrawerOpen = true;
          else if (!this.filtersActive) this.endDrawerOpen = false;
        }
      })
    );
  }

  onFilterToggle() {
    this._filterService.toggleFilters(this.drawerFilters.opened);
  }

  onEndDrawerOpenChange(isOpen: boolean) {
    this.endDrawerOpen = isOpen;
    if (this.showNotificationsDrawer) {
      if (!isOpen) {
        this.showNotificationsDrawer = false;
        this.closeNotificationsSidebar();
      }
    } else {
      this.onFilterToggle();
    }
  }

  closeSidebar() {
    this._store.dispatch(new fromApplicationActions.RemoveSidebar());
  }

  closeNotificationsSidebar() {
    // event.stopPropagation()
    this._store
      .select(applicationQuery.getNotificationsMenuOpen)
      .pipe(first())
      .subscribe(isOpen => {
        if (!isOpen) {
          return;
        }
        this._store.dispatch(new fromApplicationActions.ToggleShowingNotificationsMenu(false));
      });
  }

  /**
   * Initially set sidebar visibility based on screen size. It should be hidden
   * on mobile views unless manually opened.
   */
  initialiseSidebarVisibility() {
    if (this.isMobile) {
      this.closeSidebar();
    } else {
      this._applicationStore.dispatch(new fromApplicationActions.SetSidebar());
    }
  }

  /**
   * Conditionally show or hide the sidebar based on changes to the application
   * state.
   */
  listenToSidebarVisibility(): void {
    this._store
      .select<VisibilityState>(applicationQuery.getSidebarVisible)
      .pipe(takeUntil(this._destroy$))
      .subscribe(visibilityState => (this.sidebarVisible = visibilityState === VisibilityState.Show));
  }

  /**
   * Guarantee the current listing is loaded regardless of which route the user
   * landed on.
   *
   * The listing (which carries the `listingUuid` the rest of the app depends on)
   * is normally populated by the `ListingResolver` during navigation. Some entry
   * points — notably the enquiries landing page — do not run that resolver, so a
   * user can end up in the app with no listing in state, which breaks
   * listing-dependent features. As the shell for every authenticated
   * `/su/:listingUuid` route, this component is the route-independent place to
   * ensure the listing is fetched.
   *
   * Waits for an authenticated user (the `ListingEffects` fetch is a no-op
   * without one) then, if no listing is present, dispatches `GetListing` with
   * the URL's `listingUuid` — or with no payload for the `me` alias, matching
   * the resolver's behaviour of selecting the user's default listing.
   */
  private ensureListingLoaded(): void {
    const listingUuid = this._route.snapshot.paramMap.get('listingUuid');

    combineLatest([this._store.select(authenticationQuery.getUser), this._store.select(appQuery.getListing)])
      .pipe(
        filter(([user]) => !!user),
        first(),
        takeUntil(this._destroy$)
      )
      .subscribe(([, listing]) => {
        if (listing) {
          return;
        }

        const uuid = listingUuid && listingUuid !== 'me' ? listingUuid : undefined;
        this._store.dispatch(new fromAppActions.GetListing(uuid));
      });
  }

  /**
   * Brands the app from the active listing's brand pack, and again whenever the
   * supplier switches listing.
   */
  private applyBrandPack(): void {
    this._store
      .select(appQuery.getListing)
      .pipe(
        map(listing => listing?.uuid),
        filter((uuid): uuid is string => !!uuid),
        distinctUntilChanged(),
        switchMap(uuid => this._brandPackService.applyForListing$(uuid)),
        takeUntil(this._destroy$)
      )
      .subscribe();
  }

  private getNotifications() {
    let showInvitedIndicator: boolean = false;
    this._store
      .select<AuthUser>(authenticationQuery.getUser)
      .pipe(
        first(),
        switchMap(({ userId }) => this._notificationsService.getNotifications(userId))
      )
      .subscribe({
        next: response => {
          this._store.dispatch(new fromApplicationActions.SetUserNotifications(response));

          showInvitedIndicator = response.some(el => el.type === 'open_invitation');

          this._userStore.dispatch(
            fromUserActions.SetInvitedState({
              payload: showInvitedIndicator
            })
          );
        },
        error: err => {
          console.error('Could not get notifications: ', err);
        }
      });
  }

  /**
   * Allows us to accurately calculate what 100vh should be on mobile devices.
   * Prefer visualViewport so --vh shrinks when the virtual keyboard opens (iOS Safari
   * does not shrink window.innerHeight in that case); falls back to innerHeight when
   * visualViewport is unavailable.
   */
  private setInnerHeight() {
    const height = window.visualViewport?.height ?? window.innerHeight;
    const vh = height * 0.01;
    // Sets only --vh: <html> also carries the brand pack's variables inline.
    this._renderer.setStyle(document.documentElement, '--vh', `${vh}px`, RendererStyleFlags2.DashCase);
  }

  /**
   * visualViewport's `resize` event fires both when the soft keyboard opens AND
   * when the mobile address bar shows/hides during scroll. We only want to
   * recompute `--vh` for the keyboard case — recomputing it on address-bar
   * toggles resizes the shell mid-scroll and causes jitter (the reason this
   * listener was previously removed). Gate on an editable element being focused,
   * which is true for the keyboard case but not for address-bar scrolling.
   */
  private _onVisualViewportResize = () => {
    if (this._isEditableElementFocused()) {
      this.setInnerHeight();
    }
  };

  /**
   * Resolve the deepest focused element, piercing shadow roots (the chat's
   * `<ate-textarea-control>` is a web component, so `document.activeElement`
   * reports the host, not the inner field). Returns true when that element is a
   * text-entry field that would summon the virtual keyboard.
   */
  private _isEditableElementFocused(): boolean {
    let el: Element | null = document.activeElement;
    while (el?.shadowRoot?.activeElement) {
      el = el.shadowRoot.activeElement;
    }
    if (!el) {
      return false;
    }
    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || (el as HTMLElement).isContentEditable;
  }

  /**
   * Get the latest FCM token and update it in the RTDB.
   */
  private updateToken() {
    if (window.sessionStorage.getItem('fcmTokenSent')) {
      return;
    }

    this._fcmNotificationsService
      .registerForNotifications()
      .pipe(first())
      .subscribe({
        next: () => {
          // Set in Session Storage in order to persist within the current session.
          window.sessionStorage.setItem('fcmTokenSent', 'true');
        },
        error: err => {
          console.error('Error updating FCM token', err);
        }
      });
  }

  /**
   * @todo Only open the dialog if the cookie has not been set or.
   */
  private openFCMNotificationsDialog() {
    this._fcmNotificationsService
      .shouldDisplayModal()
      .pipe(first())
      .subscribe({
        next: shouldDisplay => {
          if (!shouldDisplay) {
            return;
          }

          this._ateModalService.open(NotificationsDialogComponent, {
            width: '500px',
            maxWidth: '95vw',
            hasCta: true,
            ctaText: 'Yes, turn on notifications',
            hasCancel: true,
            cancelText: 'Maybe later'
          });
        },
        error: err => {
          console.error('Error checking shouldDisplayModal:', err);
        }
      });
  }

  ngOnDestroy(): void {
    window.visualViewport?.removeEventListener('resize', this._onVisualViewportResize);
    this.subscriptionList.unsubscribe();
    this._destroy$.next(true);
    this._destroy$.complete();
    this._brandPackService.clear();
  }
}
